import {ConversationType} from '@toolify/server/src/models/mongoose/MessageModel/enum/ConversationType'
import {
  IConversationTypeInterfacesMap,
} from '@toolify/server/src/services/MessageService/types/IConversationTypeInterfacesMap'
import {IBreadcrumbItem} from '@toolify/client/src/modules/ui/types/IBreadcrumbItem'
import {BreadcrumbThumbnailType} from '@toolify/client/src/modules/ui/enum/BreadcrumbThumbnailType'
import {IconNameType} from '@toolify/server/src/enum/IconNameType'
import {RouteName} from '@toolify/client/src/setup/router/enum/RouteName'
import {ResourceTypeColorMap} from '@toolify/client/src/modules/resources/maps/ResourceTypeColorMap'
import {ResourceType} from '@toolify/server/src/models/mongoose/PermissionModel/enum/ResourceType'
import {i18n} from '@toolify/client/src/setup/i18n'

function createIconBreadcrumbItem(
  key: string,
  text: string,
  iconClass: IconNameType,
  iconColor: string,
  to: IBreadcrumbItem['to'],
  isCurrent: boolean,
): IBreadcrumbItem {
  return {
    key,
    text,
    to,
    isCurrent,
    thumbnailType: BreadcrumbThumbnailType.Icon,
    thumbnailPayload: {
      iconClass,
      iconColor,
    },
  }
}

function createTextBreadcrumbItem(
  key: string,
  text: string,
  to: IBreadcrumbItem['to'],
  isCurrent: boolean,
): IBreadcrumbItem {
  return {
    key,
    text,
    to,
    isCurrent,
    thumbnailType: BreadcrumbThumbnailType.None,
  }
}

export function buildActiveCallBreadcrumbs(
  conversationType: ConversationType | null | undefined,
  conversationNotificationPayload: unknown,
): IBreadcrumbItem[] {
  if(!conversationType || !conversationNotificationPayload) {
    return []
  }

  if(conversationType === ConversationType.Workspace) {
    const payload = conversationNotificationPayload as IConversationTypeInterfacesMap[ConversationType.Workspace]['notificationPayload']
    return [
      createIconBreadcrumbItem(
        'workspaceConversation',
        payload.workspaceConversationName || i18n.global.t('chat.untitledConversation'),
        IconNameType.CommentLines,
        ResourceTypeColorMap.get(ResourceType.Conversation) ?? '',
        {
          name: RouteName.Conversation,
          params: {
            workspaceName: payload.workspaceName,
            conversationId: payload.workspaceConversationId,
          },
        },
        true,
      ),
    ]
  }

  if(conversationType === ConversationType.DatabaseCell) {
    const payload = conversationNotificationPayload as IConversationTypeInterfacesMap[ConversationType.DatabaseCell]['notificationPayload']
    return [
      createIconBreadcrumbItem(
        'database',
        payload.databaseName || 'Database',
        IconNameType.Database,
        ResourceTypeColorMap.get(ResourceType.Database) ?? '',
        {
          name: RouteName.Database,
          params: {
            workspaceName: payload.workspaceName,
            databaseId: payload.databaseId,
          },
        },
        false,
      ),
      createTextBreadcrumbItem(
        'databaseItem',
        payload.databaseItemName || 'Untitled item',
        {
          name: RouteName.DatabaseItem,
          params: {
            workspaceName: payload.workspaceName,
            databaseId: payload.databaseId,
            databaseItemId: payload.databaseItemId,
          },
        },
        false,
      ),
      createTextBreadcrumbItem(
        'databaseProperty',
        payload.databasePropertyName || 'Comments',
        null,
        true,
      ),
    ]
  }

  if(conversationType === ConversationType.Direct) {
    const payload = conversationNotificationPayload as IConversationTypeInterfacesMap[ConversationType.Direct]['notificationPayload']
    return [
      createIconBreadcrumbItem(
        'directConversation',
        payload.directConversationName || 'Direct message',
        IconNameType.CommentLines,
        ResourceTypeColorMap.get(ResourceType.Conversation) ?? '',
        {
          name: RouteName.DirectConversation,
          params: {
            directConversationId: payload.directConversationId,
          },
        },
        true,
      ),
    ]
  }

  if(conversationType === ConversationType.CallRoom) {
    const payload = conversationNotificationPayload as IConversationTypeInterfacesMap[ConversationType.CallRoom]['notificationPayload']
    return [
      createIconBreadcrumbItem(
        'callRoom',
        payload.callRoomName || 'Untitled call',
        IconNameType.Microphone,
        ResourceTypeColorMap.get(ResourceType.CallRoom) ?? '',
        {
          name: RouteName.CallRoom,
          params: {
            workspaceName: payload.workspaceName,
            callRoomId: payload.callRoomId,
          },
        },
        true,
      ),
    ]
  }

  return []
}
